// src/components/wallet/RTRRewards.js
import React, { useEffect, useState, useCallback, useMemo } from "react";
import Web3 from "web3";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { Button } from "primereact/button";
import { Skeleton } from "primereact/skeleton";
import { Progress } from "@nextui-org/react";
import { formatDistanceToNow } from "date-fns";
import { formatCurrencyExtended, formatDecimals } from "../../utils";
import distributorAbi from "../../contracts/dividendToken_1_distributorAbi";

/* ═══════════════  static helpers ═══════════════ */
const lockOpts = [
  { label: "1 Month", value: 1 },
  { label: "3 Months", value: 3 },
  { label: "6 Months", value: 6 },
];
const monthToKey = { 1: "1m", 3: "3m", 6: "6m" };
const WBNB_ADDR = "0xbb4cdb9cbd36b01bd1cbaebf2de08d9173bc095c";

/* ═══════════════  component  ═══════════════ */
const RTRRewards = React.memo(
  ({
    token,
    account,
    contracts,
    tokenSelections,
    viewTotalDistributed = false,
    enableClaiming = false,
  }) => {
    /* ───── state ───── */
    const [isLoading, setIsLoading] = useState(true);
    const [isClaiming, setIsClaiming] = useState(false);
    const [isStaking, setIsStaking] = useState(false);
    const [isUnstaking, setIsUnstaking] = useState(false);

    const [contractData, setContractData] = useState(null); // totals, holders, payout tokens
    const [unclaimed, setUnclaimed] = useState(0);
    const [claimed, setClaimed] = useState(0);

    const [stakeInfo, setStakeInfo] = useState({}); // 1m/3m/6m → { amount, ts, active }
    const [countdowns, setCountdowns] = useState({}); // per-pool “in 3 days” text
    const [selDuration, setSelDuration] = useState(null); // 1 / 3 / 6
    const [amountToStake, setAmountToStake] = useState(null); // float (token units)

    const [availRaw, setAvailRaw] = useState(0); // unstaked balance (raw 10^dec)

    /* ───── helpers ───── */
    const web3 = useMemo(() => new Web3(window.ethereum), []);
    const buildCotr = useCallback(
      (addr) => new web3.eth.Contract(distributorAbi, addr),
      [web3]
    );
    const WBNBprice =
      tokenSelections.find((t) => t.address.toLowerCase() === WBNB_ADDR)
        ?.price ?? 0;
    const toUSD = (raw) => formatCurrencyExtended((raw / 1e18) * WBNBprice);

    /* ───── load-everything ───── */
    const loadAll = useCallback(async () => {
      if (!account || !contracts?.reflectr) return;
      setIsLoading(true);

      try {
        /* ---- global stats ---- */
        const [payoutTokRaw, trackerAddr] = await Promise.all([
          contracts.reflectr.methods.getPayoutToken().call(),
          contracts.reflectr.methods.dividendTracker().call(),
        ]);
        const payoutAddrs = Object.values(payoutTokRaw);
        const payoutTok = payoutAddrs.map((a) => {
          const t = tokenSelections.find(
            (x) => x.address.toLowerCase() === a.toLowerCase()
          );
          return [a, "50", t]; // weight hard-coded 50/50
        });

        const distributor = buildCotr(trackerAddr);

        const [divInfo, totalDistRaw, holdersRaw, totalRealRaw, balanceRaw] =
          await Promise.all([
            contracts.reflectr.methods.getAccountDividendsInfo(account).call(),
            contracts.reflectr.methods.getTotalDividendsDistributed().call(),
            contracts.reflectr.methods.getNumberOfDividendTokenHolders().call(),
            distributor.methods.withdrawnDividendOf(account).call(),
            contracts.reflectr.methods.balanceOf(account).call(),
          ]);

        /* ---- staking pools ---- */
        const [s1, s3, s6, ts1, ts3, ts6, act1, act3, act6] = await Promise.all(
          [
            contracts.reflectr.methods.tokensStaked1Month(account).call(),
            contracts.reflectr.methods.tokensStaked3Months(account).call(),
            contracts.reflectr.methods.tokensStaked6Months(account).call(),
            contracts.reflectr.methods._staked1MonthTimestamp(account).call(),
            contracts.reflectr.methods._staked3MonthsTimestamp(account).call(),
            contracts.reflectr.methods._staked6MonthsTimestamp(account).call(),
            contracts.reflectr.methods._isStaked1Month(account).call(),
            contracts.reflectr.methods._isStaked3Months(account).call(),
            contracts.reflectr.methods._isStaked6Months(account).call(),
          ]
        );

        /* ---- state updates ---- */
        setUnclaimed(Number(divInfo[3]));
        setClaimed(Number(totalRealRaw));
        setContractData({
          totalDistributed: Number(totalDistRaw),
          dividendHolders: Number(holdersRaw),
          payoutTokens: payoutTok,
        });

        setStakeInfo({
          "1m": { amount: Number(s1), ts: Number(ts1), active: act1 },
          "3m": { amount: Number(s3), ts: Number(ts3), active: act3 },
          "6m": { amount: Number(s6), ts: Number(ts6), active: act6 },
        });

        const stakedSum = Number(s1) + Number(s3) + Number(s6);
        setAvailRaw(Math.max(Number(balanceRaw) - stakedSum, 0));
      } catch (e) {
        console.error("RTR load error", e);
      }

      setIsLoading(false);
    }, [account, contracts?.reflectr, tokenSelections, buildCotr]);

    /* initial + 60 s refresh */
    useEffect(() => {
      loadAll();
    }, [loadAll]);
    useEffect(() => {
      const id = setInterval(loadAll, 60_000);
      return () => clearInterval(id);
    }, [loadAll]);

    /* ---- per-pool countdowns ---- */
    useEffect(() => {
      const id = setInterval(() => {
        const now = Date.now();
        const cd = {};
        Object.entries(stakeInfo).forEach(([k, info]) => {
          if (!info?.active) cd[k] = "Not staked";
          else {
            const months = k === "1m" ? 1 : k === "3m" ? 3 : 6;
            const endMs = (info.ts + months * 4 * 7 * 24 * 60 * 60) * 1000;
            cd[k] =
              now < endMs
                ? formatDistanceToNow(new Date(endMs), { addSuffix: true })
                : "Unlock ready";
          }
        });
        setCountdowns(cd);
      }, 1000);
      return () => clearInterval(id);
    }, [stakeInfo]);

    /* ---- earliest unlock header ---- */
    const earliestUnlock = useMemo(() => {
      const unlockTs = ["1m", "3m", "6m"]
        .filter((k) => stakeInfo[k]?.active)
        .map((k) => {
          const months = k === "1m" ? 1 : k === "3m" ? 3 : 6;
          return stakeInfo[k].ts + months * 4 * 7 * 24 * 60 * 60;
        })
        .filter((t) => t > Date.now() / 1000);
      if (unlockTs.length === 0) return "Not Staked";
      const soonest = Math.min(...unlockTs) * 1000;
      return formatDistanceToNow(new Date(soonest), { addSuffix: true });
    }, [stakeInfo]);

    /* ---- claim ---- */
    const handleClaim = async () => {
      if (!contracts?.reflectr || isClaiming) return;
      setIsClaiming(true);
      try {
        await contracts.reflectr.methods
          .claim()
          .send({ from: account, gas: 1_750_000 });
        await loadAll();
      } catch (e) {
        console.error("RTR claim error", e);
      }
      setIsClaiming(false);
    };

    /* ---- stake ---- */
    const stakeMethod = (d) =>
      d === 1
        ? "StakeFor1Month"
        : d === 3
        ? "StakeFor3Months"
        : "StakeFor6Months";
    const handleStake = async () => {
      if (!selDuration || !amountToStake || isStaking) return;
      setIsStaking(true);
      try {
        const rawAmt = web3.utils.toBN(Math.floor(amountToStake)).toString();
        await contracts.reflectr.methods[stakeMethod(selDuration)](rawAmt).send(
          { from: account }
        );
        setAmountToStake(null);
        setSelDuration(null);
        await loadAll();
      } catch (e) {
        console.error("RTR stake error", e);
      }
      setIsStaking(false);
    };

    /* ---- unstake ---- */
    const unstakeMethod = (d) =>
      d === 1 ? "Unstake1Month" : d === 3 ? "Unstake3Months" : "Unstake6Months";
    const handleUnstake = async (d) => {
      if (isUnstaking) return;
      setIsUnstaking(true);
      try {
        await contracts.reflectr.methods[unstakeMethod(d)]().send({
          from: account,
        });
        await loadAll();
      } catch (e) {
        console.error("RTR unstake error", e);
      }
      setIsUnstaking(false);
    };

    /* ═══════════════  render  ═══════════════ */
    return (
      <div
        style={{
          borderRadius: 10,
          background: "rgba(50,100,255,0.05)",
          padding: 5,
          fontSize: 11,
        }}
      >
        {enableClaiming && (
          <>
            {/* --- header: available & earliest unlock --- */}
            <div style={{ marginBottom: 4, minHeight: "1rem" }}>
              Available To Stake:&nbsp;
              <strong style={{ width: "4ch", display: "inline-block" }}>
                {isLoading ? (
                  <Skeleton width="4ch" height="1rem" />
                ) : (
                  Math.floor(availRaw / 10 ** token.decimals).toFixed(0)
                )}
              </strong>
              &nbsp;|&nbsp; Unstake:&nbsp;
              <strong style={{ width: "16ch", display: "inline-block" }}>
                {isLoading ? (
                  <Skeleton width="16ch" height="1rem" />
                ) : (
                  earliestUnlock
                )}
              </strong>
            </div>
          </>
        )}
        {/* --- payout tokens line --- */}
        <div style={{ marginBottom: 4 }}>
          Rewards:&nbsp;
          <strong>
            {contractData ? (
              contractData.payoutTokens
                .map((p) => `${p[1]}% ${p[2].label}`)
                .join(", ")
            ) : (
              <Skeleton width="120px" height="1rem" />
            )}
          </strong>
        </div>

        {viewTotalDistributed && (
          <div>
            Total Distributed:&nbsp;
            <strong>
              {contractData ? (
                toUSD(contractData.totalDistributed)
              ) : (
                <Skeleton width="100px" height="1rem" />
              )}
            </strong>
          </div>
        )}

        {contractData?.dividendHolders > 0 && (
          <div>
            Holder Count:&nbsp;<strong>{contractData.dividendHolders}</strong>
          </div>
        )}

        {claimed === 0 && unclaimed === 0 && (
          <div>
            <strong>No rewards pending</strong>
          </div>
        )}

        {claimed > 0 && (
          <div>
            Earned:&nbsp;<strong>{toUSD(claimed)}</strong>
          </div>
        )}

        {unclaimed > 0 && (
          <div>
            Pending:&nbsp;<strong>{toUSD(unclaimed)}</strong>
          </div>
        )}

        {/* --- claim button --- */}
        {unclaimed > 0 &&
          enableClaiming &&
          (isClaiming ? (
            <Progress indeterminate style={{ marginTop: 10 }} />
          ) : (
            <Button
              rounded
              severity="warning"
              size="small"
              style={{ marginTop: 10, width: 200, justifyContent: "center" }}
              onClick={handleClaim}
              disabled={isClaiming}
            >
              Claim
            </Button>
          ))}

        <hr style={{ margin: "8px 0" }} />
        {enableClaiming && (
          <>
            {/* --- stake controls --- */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                flexWrap: "wrap",
              }}
            >
              <Dropdown
                value={selDuration}
                options={lockOpts}
                onChange={(e) => setSelDuration(e.value)}
                placeholder="Lock"
                style={{ width: 110 }}
                disabled={isStaking}
              />
              <InputNumber
                value={amountToStake}
                mode="decimal"
                useGrouping={false}
                onValueChange={(e) => setAmountToStake(e.value)}
                placeholder="Amount"
                min={0}
                maxFractionDigits={token.decimals}
                style={{ width: 110 }}
                disabled={isStaking}
              />
              <Button
                label="Stake"
                icon="pi pi-lock"
                size="small"
                severity="secondary"
                disabled={isStaking || !selDuration || !amountToStake}
                onClick={handleStake}
              />
            </div>

            {/* --- active locks --- */}
            {["1", "3", "6"].map(
              (m) =>
                stakeInfo[monthToKey[m]]?.active && (
                  <div key={m} style={{ marginTop: 6 }}>
                    <strong>{m} M</strong>&nbsp;|&nbsp; Staked:&nbsp;
                    {formatDecimals(
                      stakeInfo[monthToKey[m]].amount,
                      token.decimals,
                      2
                    )}
                    &nbsp;|&nbsp;
                    {countdowns[monthToKey[m]] || "…"}
                    {countdowns[monthToKey[m]] === "Unlock ready" &&
                      (isUnstaking ? (
                        <Progress
                          indeterminate
                          style={{
                            width: 60,
                            display: "inline-block",
                            marginLeft: 6,
                          }}
                        />
                      ) : (
                        <Button
                          label="Unstake"
                          size="small"
                          style={{ marginLeft: 6 }}
                          onClick={() => handleUnstake(Number(m))}
                        />
                      ))}
                  </div>
                )
            )}
          </>
        )}
      </div>
    );
  }
);

export default RTRRewards;

// import React, { useEffect, useState } from "react";
// import { formatCurrencyExtended, formatDecimals } from '../../utils';
// import { Button } from 'primereact/button';
// import { Skeleton } from 'primereact/skeleton';
// import { Progress } from '@nextui-org/react';
// import { formatDistanceToNow, set } from 'date-fns';
// import distributorAbi from '../../contracts/dividendToken_1_distributorAbi.js';
// import Web3 from 'web3';

// const stakingOptions = [
//     { duration: "1 Month", value: "1" },
//     { duration: "3 Months", value: "3" },
//     { duration: "6 Months", value: "6" },
// ];

// const RTRRewards = React.memo(({ token, account, contracts, tokenSelections, viewTotalDistributed = false, enableClaiming = false, refreshData }) => {
//     const [unclaimedRewards, setUnclaimedRewards] = useState(0);
//     const [totalDistributed, setTotalDistributed] = useState(0);
//     const [totalRealised, setTotalRealised] = useState(0);
//     const [isClaiming, setIsClaiming] = useState(false);
//     const [isStaking, setIsStaking] = useState(false);
//     const [isUnstaking, setIsUnstaking] = useState(false);
//     const [tokensToStake, setTokensToStake] = useState(0);
//     const [stakingData, setStakingData] = useState({ '1m': 0, '3m': 0, '6m': 0 });
//     const [stakingTime, setStakingTime] = useState("");
//     const [unstakingTime, setUnstakingTime] = useState("");
//     const [availableToStake, setAvailableToStake] = useState(0);
//     const [activeIndex, setActiveIndex] = useState(0);
//     const [countdownTimers, setCountdownTimers] = useState({ '1m': '', '3m': '', '6m': '' });
//     const [contractData, setContractData] = useState(null);
//     const [claimedDividends, setClaimedDividends] = useState();
//     const [unclaimedDividends, setUnclaimedDividends] = useState();

//     const buildDistributorContract = async (distributoraddress) => {
//         const web3 = new Web3(window.ethereum);
//         const contractInstance = new web3.eth.Contract(distributorAbi, distributoraddress);
//         return contractInstance;
//     }

//     useEffect(() => {

//         const fetchContractData = async () => {

//             let payoutTokens = await contracts.reflectr.methods.getPayoutToken().call();

//             payoutTokens = Object.values(payoutTokens);
//             payoutTokens = payoutTokens.map(address => [address, "50", true]);
//             //search tokenselections and append all the data into payoutTokens, seperate from address & weight
//             payoutTokens = payoutTokens.map(token => {
//                 const tokenData = tokenSelections.find(t => t.address.toLowerCase() === token[0].toLowerCase());
//                 if (tokenData) {
//                     return [token[0], token[1], tokenData];
//                 }
//                 return token;
//             });
//             //console.log('payoutTokens', payoutTokens);

//             const distributor = await buildDistributorContract(await contracts.reflectr.methods.dividendTracker().call());
//             const totalRealised = await distributor.methods.withdrawnDividendOf(account).call();
//             console.log('totalRealised', totalRealised);

//             return {
//                 accountDividendsInfo: await contracts.reflectr.methods.getAccountDividendsInfo(account).call(),
//                 dividendTokenBalance: await contracts.reflectr.methods.dividendTokenBalanceOf(account).call(),
//                 totalDividendsDistributed: await contracts.reflectr.methods.getTotalDividendsDistributed().call(),
//                 payoutTokens,
//                 totalRealised: totalRealised,
//                 dividendHolders: await contracts.reflectr.methods.getNumberOfDividendTokenHolders().call(),
//             }
//         }

//         // const fetchRewards = async (tokenAddress, weight) => {
//         //     if (!contracts?.reflectr || !account) return;
//         //     // const reflectionPool = await contracts?.ultraDistributor.methods.reflectionPoolAtERC20(token?.address).call();
//         //     // const unclaimed = await contracts?.ultraDistributor.methods.getTotalUnrealisedWithRealisedUndeposited(account, token?.address).call();
//         //     // const totalDist = reflectionPool?.data.totalDistributed;
//         //     // const totalReal = await contracts?.ultraDistributor.methods.shares(account, token?.address).call();
//         //     // setUnclaimedRewards(unclaimed);
//         //     // setTotalDistributed(totalDist);
//         //     // setTotalRealised(totalReal.totalRealised);
//         // };

//         // fetchRewards();
//         fetchContractData().then(setContractData);

//     }, [contracts?.reflectr, account, token?.address, refreshData]);

//     useEffect(() => {
//         // Async function to fetch staking data
//         const fetchStakingData = async () => {
//             if (!contracts.reflectr) return;

//             try {
//                 const result_1mo = await contracts.reflectr.methods.tokensStaked1Month(account).call();
//                 const result_3mo = await contracts.reflectr.methods.tokensStaked3Months(account).call();
//                 const result_6mo = await contracts.reflectr.methods.tokensStaked6Months(account).call();
//                 const result_1mo_timestamp = await contracts.reflectr.methods._staked1MonthTimestamp(account).call();
//                 const result_3mo_timestamp = await contracts.reflectr.methods._staked3MonthsTimestamp(account).call();
//                 const result_6mo_timestamp = await contracts.reflectr.methods._staked6MonthsTimestamp(account).call();
//                 const result_1mo_stakingActive = await contracts.reflectr.methods._isStaked1Month(account).call();
//                 const result_3mo_stakingActive = await contracts.reflectr.methods._isStaked3Months(account).call();
//                 const result_6mo_stakingActive = await contracts.reflectr.methods._isStaked6Months(account).call();

//                 // Update state with fetched data
//                 setStakingData({
//                     '1m': result_1mo,
//                     '3m': result_3mo,
//                     '6m': result_6mo,
//                     '1m_timestamp': result_1mo_timestamp,
//                     '3m_timestamp': result_3mo_timestamp,
//                     '6m_timestamp': result_6mo_timestamp,
//                     '1m_stakingActive': result_1mo_stakingActive,
//                     '3m_stakingActive': result_3mo_stakingActive,
//                     '6m_stakingActive': result_6mo_stakingActive,
//                 });
//             } catch (error) {
//                 console.error("Error fetching staking data:", error);
//             }
//         };

//         fetchStakingData();
//     }, [token?.address, account, contracts?.reflectr]);

//     useEffect(() => {
//         // Function to update countdown timers
//         const updateCountdowns = () => {
//             const newTimers = { ...countdownTimers };
//             Object.keys(stakingData).forEach(key => {
//                 if (key.includes('_timestamp')) {
//                     const timestamp = stakingData[key];
//                     // Ensure timestamp is a valid number and not null or undefined
//                     if (timestamp && !isNaN(timestamp)) {
//                         const durationMonths = key.includes('1m') ? 1 : key.includes('3m') ? 3 : 6;
//                         const endTimestamp = parseInt(timestamp) * 1000 + durationMonths * 4 * 7 * 24 * 60 * 60 * 1000; // Ensure timestamp is in milliseconds
//                         const now = Date.now();
//                         if (now < endTimestamp) {
//                             newTimers[key.replace('_timestamp', '')] = formatDistanceToNow(new Date(endTimestamp), { addSuffix: true });
//                         } else {
//                             newTimers[key.replace('_timestamp', '')] = 'Staking period ended';
//                             //console.log("Staking ended for", key.replace('_timestamp', ''));
//                         }
//                     } else {
//                         // Handle invalid or missing timestamp
//                         newTimers[key.replace('_timestamp', '')] = 'Unavailable';
//                     }
//                 }
//             });
//             setCountdownTimers(newTimers);
//         };

//         // Update countdown every second
//         const intervalId = setInterval(updateCountdowns, 1000);

//         // Clean up interval on component unmount
//         return () => clearInterval(intervalId);
//     }, [stakingData]);

//     useEffect(() => {
//         if (!contractData) return;
//         setUnclaimedRewards(contractData.accountDividendsInfo[3]);
//         setClaimedDividends(contractData.totalRealised);
//     }, [contractData]);

//     const handleClaim = async () => {
//         setIsClaiming(true);

//         try {
//             const transaction = await contracts.reflectr.methods.claim().send({ from: account, gas: 1750000 });
//             refreshData();
//             setIsClaiming(false);

//         } catch (error) {
//             // console.error("Error claiming rewards:", error);
//             setIsClaiming(false);
//         }
//     };

//     const handleStake = async () => {
//         setIsStaking(true);
//         if (!contracts.reflectr) {
//             console.error("ReflectR contract not available.");
//             return;
//         }

//         try {
//             let transaction;

//             switch (stakingTime) {
//                 case "1": // 1 Month
//                     transaction = contracts.reflectr.methods.StakeFor1Month(tokensToStake).send({ from: account });
//                     break;
//                 case "3": // 3 Months
//                     transaction = contracts.reflectr.methods.StakeFor3Months(tokensToStake).send({ from: account });
//                     break;
//                 case "6": // 6 Months
//                     transaction = contracts.reflectr.methods.StakeFor6Months(tokensToStake).send({ from: account });
//                     break;
//                 default:
//                     console.error("Invalid staking duration selected");
//                     return;
//             }

//             const tx = await transaction;

//             setTokensToStake(0);
//         } catch (error) {
//             console.error("Error interacting with the ReflectR contract:", error);
//         }
//         setIsStaking(false);
//     };

//     const WBNB = "0xbb4CdB9CBd36B01bD1cBaEBF2De08d9173bc095c";
//     const WBNB_price = tokenSelections.find(token => token.address.toLowerCase() === WBNB.toLowerCase())?.price;

//     return (
//         <div style={{
//             alignItems: 'center',
//             borderRadius: '10px',
//             backgroundColor: 'rgba(50, 100, 255, 0.05)',
//             padding: '5px',
//             fontSize: '11px',
//         }}>

//             <div>Rewards: <strong>{contractData?.payoutTokens?.map(token => token[1] + '% ' + token[2].label).join(', ')}</strong></div>
//             {viewTotalDistributed &&
//                 <div>Total Distributed:  <strong>
//                     {contractData?.totalDividendsDistributed ? (
//                         formatCurrencyExtended(contractData?.totalDividendsDistributed / 10 ** 18 * WBNB_price)
//                     ) : (
//                         <Skeleton width="100%" height="12px" className="mb-2" />
//                     )}
//                 </strong>
//                 </div>}

//             {contractData?.dividendHolders > 0 && <div>Holder Count:  <strong>
//                 {contractData?.dividendHolders ? (
//                     contractData?.dividendHolders
//                 ) : (
//                     <Skeleton width="100%" height="12px" className="mb-2" />
//                 )}</strong>
//             </div>}
//             {
//                 parseInt(contractData?.totalRealised) === 0 && parseInt(unclaimedRewards) === 0 && <div><strong>No rewards pending</strong></div>
//             }
//             {contractData?.totalRealised > 0 && <div>Earned:  <strong>
//                 {contractData?.totalRealised ? (
//                     formatCurrencyExtended(contractData.totalRealised / 10 ** 18 * WBNB_price)
//                 ) : (
//                     <Skeleton width="100%" height="12px" className="mb-2" />
//                 )}</strong>
//             </div>}
//             {unclaimedRewards > 0 && <div>Pending:  <strong>
//                 {unclaimedRewards ? (
//                     formatCurrencyExtended(unclaimedRewards / 10 ** 18 * WBNB_price)
//                 ) : (
//                     <Skeleton width="100%" height="12px" className="mb-2" />
//                 )}</strong>
//             </div>}
//             {unclaimedRewards > 0 &&
//             enableClaiming && (isClaiming ? (
//                 <Progress indeterminate style={{ marginTop: '10px' }} />
//             ) : (
//                 <Button
//                     rounded
//                     severity="warning"
//                     onClick={handleClaim}
//                     size="small"
//                     style={{
//                         marginTop: '10px',
//                         alignSelf: 'center',
//                         width: '200px',
//                         justifyContent: 'center',
//                     }}
//                     disabled={isClaiming}
//                 >
//                     Claim
//                 </Button>
//             ))}

//         </div>
//     );
// });

// export default RTRRewards;
